import { KatapultDialog } from '../elements/katapult-elements/katapult-dialog.js';
import { html } from 'lit';

const TOKEN_PREFIX_DELIMITER = '$(';
const TOKEN_SUFFIX_DELIMITER = ')';
const TOKEN_REGEX = /(?<=\$\()[_\-a-zA-Z0-9]+(?=\))/g;

/**
 * @typedef {(token: string) => string | Promise<string>} TokenResolver
 */

/**
 * Given a tokenized url, a set unique tokens will be returned.
 * @param {string} url
 * @returns {string[]}
 * @throws {TypeError} If url is not a string.
 */
function getTokensFromUrl(url) {
  if (typeof url !== 'string') throw new TypeError('url must be a string');
  const iterator = url.matchAll(TOKEN_REGEX);
  const allTokenInstances = Array.from(iterator, (match) => match[0]);
  const uniqueTokenInstances = [...new Set(allTokenInstances)];
  return uniqueTokenInstances;
}

/**
 * Given a tokenized url and a token resolver function, a url will be returned where all tokens are replaced with their resolved values.
 * @param {string} url
 * @param {TokenResolver} resolveToken - A function returns a value for a given token.
 * @returns {Promise<Object<string, string>>} - A promise which resolves to an object where the keys are tokens and the values are the resolved values.
 */
async function getTokenValuesFromUrl(url, resolveToken) {
  const tokenValues = /** @type { Object<string, string> } */ ({});

  const tokens = getTokensFromUrl(url);
  for (const token of tokens) {
    const value = await resolveToken(token);
    tokenValues[token] = value;
  }

  return tokenValues;
}

/**
 * Given a tokenized url and an array of resolved tokens, a url will be returned where all tokens are replaced with their resolved values.
 * @param {string} url
 * @param {Object<string, string>} tokenValues - An object where the keys are tokens and the values are the resolved values.
 * @returns {string} - The url in which all tokens have been replaced with their resolved values.
 * @throws {TypeError} If url is not a string.
 */
function replaceUrlTokens(url, tokenValues) {
  if (typeof url !== 'string') throw new TypeError('url must be a string');
  for (const [token, value] of Object.entries(tokenValues)) {
    const enclosedToken = `${TOKEN_PREFIX_DELIMITER}${token}${TOKEN_SUFFIX_DELIMITER}`;
    url = url.replaceAll(enclosedToken, encodeURIComponent(value));
  }
  return url;
}

/**
 * Given a tokenized url and a token resolver function, a url will be returned where all tokens are replaced with their resolved values.
 * @param {string} url
 * @param {TokenResolver} resolveToken - A function returns a value for a given token.
 * @returns {Promise<string>} - The url in which all tokens have been replaced with their resolved values.
 */
async function resolveUrl(url, resolveToken) {
  const tokenValues = await getTokenValuesFromUrl(url, resolveToken);

  if ('api_key' in tokenValues && tokenValues.api_key == null)
    throw new Error('This tool requires a valid Katapult Pro API key to run. Please ensure you have a valid API key and try again.');

  return replaceUrlTokens(url, tokenValues);
}

/**
 * Prompts the user to confirm that they want to run a tool unless they have previously confirmed that they wish to run the tool.
 * @param {string} toolId
 * @returns {Promise<boolean>}
 * @throws {TypeError} If toolId is not a string.
 * @throws {Error} If toolId is an empty string.
 */
async function confirmMayRunTool(toolId) {
  if (typeof toolId !== 'string') throw new TypeError('toolId must be a string');
  if (toolId.length === 0) throw new Error('toolId must not be an empty string');

  const { uid } = globalThis.firebase.auth().currentUser ?? {};

  const toolLocalStoragePath = ['may_run_api_tool_from_model_without_confirmation', uid, toolId].filter((x) => !!x).join('_');

  const mayRunToolWithoutConfirmation = localStorage.getItem(toolLocalStoragePath) === 'true';
  if (mayRunToolWithoutConfirmation) return true;

  const shouldRunTool = await KatapultDialog.confirm({
    text: `This tool will send data to an external source outside of this application. Be sure that you trust the creator of this tool.`,
    confirmButton: { label: `Continue` }
  }).confirmed;

  if (shouldRunTool) {
    localStorage.setItem(toolLocalStoragePath, 'true');
    return true;
  }

  return false;
}

/**
 * @typedef {Object} IframeConfig
 * @property {string} width - The width of the iframe dialog.
 * @property {string} height - The height of the iframe dialog.
 * @property {string} minWidth - The minimum width of the iframe dialog.
 * @property {string} minHeight - The minimum height of the iframe dialog.
 * @property {string} maxWidth - The maximum width of the iframe dialog.
 * @property {string} maxHeight - The maximum height of the iframe dialog.
 * @property {boolean} interactive - Whether the iframe dialog is resizable and draggable
 * @property {string} theme_color - The theme color for the iframe header.
 * @property {string} text_color - The text color for the iframe header.
 * @property {boolean} [pinnable] - Whether the dialog can be pinned (shows pin button)
 * @property {string} [pin_location] - The default pin location: 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'bottom-middle', 'auto-pin'
 * @property {boolean} [pin_in_map] - Whether the dialog is pinned in the map (if true, centerElement is required)
 * @property {boolean} [toggle_border] - Whether the dialog shows border toggle functionality
 */

/**
 * @typedef {Object} WebToolModel
 * @property {string} [id] - The id of the tool.
 * @property {string} label - The name of the tool.
 * @property {string} endpoint_url - The url of the tool.
 * @property {string} endpoint_action - Whether the tool should be opened in a new tab or not.
 * @property {IframeConfig} [iframe_config] - Configuration for iframe display.
 */

/**
 * @param {WebToolModel} webToolModel
 * @param {TokenResolver} resolveToken
 * @param {Element?} centerElement - Element to center the dialog on
 * @returns {Promise<void>}
 */
async function runWebTool(webToolModel, resolveToken, centerElement) {
  const {
    label = 'Web Tool',
    id = label,
    endpoint_url: endpointUrl,
    endpoint_action: endpointAction,
    iframe_config: iframeConfig
  } = webToolModel;
  if (!endpointUrl) throw new Error('endpoint_url is missing from the web tool model');
  if (!endpointAction) throw new Error('endpoint_action is missing from the web tool model');

  const mayRunTool = await confirmMayRunTool(id);
  if (!mayRunTool) return;

  try {
    const resolvedUrl = await resolveUrl(endpointUrl, resolveToken);

    switch (endpointAction) {
      case 'API_CALL':
        await fetch(resolvedUrl, { method: 'POST' }).then(() => {
          KatapultDialog.alert({ text: `${label} was run successfully`, dialog: { title: `${label} Succeeded`, icon: 'check_circle' } });
        });
        break;
      case 'OPEN_LINK_IN_NEW_TAB':
        openWindow(resolvedUrl);
        break;
      case 'OPEN_LINK_IN_EMBEDDED_WINDOW':
        openInIframe(resolvedUrl, label, iframeConfig, centerElement);
        break;
      default:
        throw new Error(`Unknown endpoint_action: ${endpointAction}`);
    }
  } catch (error) {
    KatapultDialog.alert({
      text: `An error occurred while running ${label}:\n\n${error.message}`,
      dialog: { title: `${label} Failed`, icon: 'error', color: 'var(--sl-color-danger)' }
    });
  }
}

/**
 * Opens the url in a new tab
 * @param {string} url
 */
function openWindow(url) {
  const urlProtocol = url.match(new RegExp('.+://'))?.[0];
  const urlHasProtocol = urlProtocol !== undefined;
  const protocolStartsWithHttps = !!urlProtocol?.startsWith('https');
  // If we have a non https protocol, open the url in the same tab using location.href
  if (urlHasProtocol == true && protocolStartsWithHttps == false) {
    window.location.href = url;
  }
  // Otherwise, open it in a new tab
  else {
    window.open(url, '_blank');
  }
}

/**
 * Opens an iframe with the specified URL and configuration.
 * @param {string} url - the URL to open in the iframe
 * @param {string} title - the title of the iframe dialog
 * @param {IframeConfig} [config] - configuration for the iframe dialog. If not provided, defaults will be used.
 * @param {Element?} centerElement - the element to center the dialog on
 * @returns {Promise<KatapultDialog>} - a Promise that resolved with the opened dialog instance
 */
async function openInIframe(url, title = 'Web Tool', config, centerElement = null) {
  const {
    width = '30%',
    height = '30%',
    minWidth = '20%',
    minHeight = '20%',
    maxWidth = '95%',
    maxHeight = '95%',
    interactive = true,
    theme_color = '#007299',
    text_color = '#F2F2F2',
    pinnable = false,
    pin_location = null,
    pin_in_map = false,
    toggle_border = false
  } = config || {};

  // Check if an iframe with the same title already exists
  /** @type KatapultDialog? */
  const existingDialog = document.querySelector(`katapult-dialog[data-iframe-title="${title}"]`);
  if (existingDialog) {
    // Bring existing dialog to front and pulse
    existingDialog.resetPosition();
    existingDialog.pulse();
    return existingDialog;
  }

  // Convert percentage dimensions to pixel values for KatapultDialog
  const { finalWidth, finalHeight } = convertDimensionsToPixels(width, height);
  const { finalWidth: finalMinWidth, finalHeight: finalMinHeight } = convertDimensionsToPixels(minWidth, minHeight);
  const { finalWidth: finalMaxWidth, finalHeight: finalMaxHeight } = convertDimensionsToPixels(maxWidth, maxHeight);

  // Handle auto-pin functionality from dropdown
  const isAutoPin = pin_location === 'auto-pin';
  const finalPinLocation = isAutoPin ? null : pin_location;

  // Detect mobile devices and adjust width accordingly
  const mobileWidth = Math.round(window.innerWidth * 0.75);
  const responsiveWidth = globalThis.isMobile ? mobileWidth : finalWidth;

  const dialog = KatapultDialog.open({
    dialog: {
      title: title,
      color: theme_color,
      textColor: text_color,
      closeButton: true,
      draggable: interactive,
      width: responsiveWidth,
      height: finalHeight,
      minWidth: finalMinWidth,
      minHeight: finalMinHeight,
      maxWidth: finalMaxWidth,
      maxHeight: finalMaxHeight,
      closeOnEscape: true,
      closeOnOutsideClick: false,
      modal: false,
      resizable: interactive,
      centerElement: pin_in_map ? centerElement : null,
      boundaryElement: pin_in_map && centerElement ? centerElement : null,
      pinnable: pinnable,
      pinLocation: finalPinLocation,
      autoPin: isAutoPin,
      toggleBorder: toggle_border
    },
    template: () => html`
      <div
        slot="before-scroll"
        style="width: 100%; height: 100%; display: flex; flex-direction: column; overflow: hidden; position: relative;"
      >
        <iframe
          src="${url}"
          style="width: 100%; height: 100%; border: none; display: block; background: white; position: relative; z-index: 1; min-width: ${finalMinWidth}px; min-height: ${finalMinHeight}px; flex: 1;"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-popups-to-escape-sandbox"
        ></iframe>
      </div>
    `
  });

  // Add identifier for reuse detection
  dialog.setAttribute('data-iframe-title', title);

  await dialog.openStart;
  const frameWindow = dialog.querySelector('iframe')?.contentWindow;
  const messageListener = (e) => {
    if (e.source !== frameWindow) return;
    if (e.data?.katapultDialogAction === 'close') dialog.close();
  };
  window.addEventListener('message', messageListener);
  dialog.closeComplete.then(() => window.removeEventListener('message', messageListener));

  return dialog;
}

/**
 * Converts width and height values to pixel values.
 * @param {string|number} width - the width value, can be a percentage string, pixel string, or number
 * @param {string|number} height - the height value, can be a percentage string, pixel string, or number
 * @returns {{ finalWidth: number, finalHeight: number }} - an object containing the final width and height in pixels
 */
function convertDimensionsToPixels(width, height) {
  // Helper function to convert percentage strings to pixel values
  const convertPercentageToPixels = (value, dimension) => {
    if (typeof value === 'string' && value.includes('%')) {
      const percentage = parseInt(value.replace('%', ''));
      const maxDimension = dimension === 'width' ? window.innerWidth : window.innerHeight;
      return Math.round((percentage / 100) * maxDimension);
    }
    if (typeof value === 'string' && value.includes('px')) {
      return parseInt(value.replace('px', ''));
    }
    if (typeof value === 'number') {
      return value;
    }
    // Default fallback
    return dimension === 'width' ? Math.round(window.innerWidth * 0.3) : Math.round(window.innerHeight * 0.4);
  };

  const finalWidth = convertPercentageToPixels(width, 'width');
  const finalHeight = convertPercentageToPixels(height, 'height');

  return { finalWidth, finalHeight };
}

export { getTokensFromUrl, runWebTool, openInIframe };
